/**
 * client/src/features/income/components/IncomeCards/OtherIncomeCard.jsx
 * 
 * Generic card component for other income types (rental, business, investments, etc.)
 * Provides a simpler view with basic tax estimation.
 */
import React, { useState } from 'react';
import { 
  TrashIcon, 
  PencilIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CurrencyDollarIcon,
  BuildingOfficeIcon,
  CalendarIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
  CheckIcon,
  XMarkIcon,
  UserIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import { formatCurrency, formatDate, getFrequencyLabel, getIncomeTypeIcon, normalizeIncomeData } from '../../utils/incomeUtils';
import { calculateTaxSummary, formatTaxSummary } from '../../utils/taxCalculator';
import useIncome from '../../hooks/useIncome';
import { useAuthContext } from '../../../auth/context/AuthContext';

// US States options for dropdown
const STATE_OPTIONS = [
  { value: '', label: 'Select State' },
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'DC', label: 'District of Columbia' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' }
];

// Helper function to extract income ID as string
function extractIncomeId(income) {
  if (!income) return null;
  
  // Handle string IDs
  if (typeof income._id === 'string') {
    return income._id;
  }
  
  // Handle MongoDB ObjectId objects
  if (income._id && typeof income._id === 'object') {
    return income._id.toString ? income._id.toString() : String(income._id);
  }
  
  // Handle nested ID structures
  if (income.id) {
    return typeof income.id === 'string' ? income.id : String(income.id);
  }
  
  console.warn('⚠️ Could not extract income ID from:', income);
  return null;
}

/**
 * OtherIncomeCard Component
 * 
 * Displays details for other income types (rental, business, investments, etc.)
 * Provides a more generic display with basic tax estimation.
 * 
 * @param {Object} props
 * @param {Object} props.income - Income object with details
 * @param {Function} props.refreshIncomes - Function to refresh income data
 * @param {Function} props.onEdit - Function to handle editing this income
 */
const OtherIncomeCard = ({ income, refreshIncomes, onEdit, className = '' }) => {
  // Component state
  const [expanded, setExpanded] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Edit mode state (inline editing in expanded details)
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  
  // Get income management functions from custom hook
  const { deleteIncome, updateIncome } = useIncome();
  
  // Get user context for partner income detection
  const { user } = useAuthContext();
  
  // Check if this is partner income and get owner name
  // Income has 'owner' field (who it belongs to) and 'user' field (for querying)
  const incomeOwnerId = income?.owner?._id || income?.owner || income?.user;
  const currentUserId = user?._id || user?.id;
  const isPartnerIncome = incomeOwnerId && currentUserId && incomeOwnerId.toString() !== currentUserId.toString();
  const ownerName = isPartnerIncome 
    ? (income?.owner?.firstName || income?.createdBy?.firstName || user?.partner?.firstName || user?.createdPartner?.firstName || 'Partner')
    : null;
  
  // Initialize edit form data
  const initializeEditForm = () => {
    const typeSpecificDetails = income.typeSpecificDetails || {};
    
    setEditFormData({
      name: income.name || '',
      grossAmount: income.amount || income.grossAmount || '',
      frequency: income.frequency || 'monthly',
      state: income.state || '',
      startDate: income.startDate ? income.startDate.split('T')[0] : '',
      endDate: income.endDate ? income.endDate.split('T')[0] : '',
      source: income.source || '',
      description: income.description || '',
      
      // Tax withholdings (will be calculated)
      federalTax: 0,
      stateTax: 0,
      totalTax: 0,
      netAmount: 0
    });
  };
  
  // Handle edit form field changes with automatic tax calculations
  const handleEditFieldChange = (field, value) => {
    const updatedData = { ...editFormData, [field]: value };
    
    // Trigger tax recalculation for relevant fields
    if (['grossAmount', 'frequency', 'state'].includes(field)) {
      const taxCalculationData = {
        grossAmount: parseFloat(updatedData.grossAmount) || 0,
        frequency: updatedData.frequency || 'monthly',
        state: updatedData.state || '',
        category: 'OTHER'
      };
      
      console.log('🧮 Calculating taxes for other income with data:', taxCalculationData);
      const taxSummary = calculateTaxSummary(taxCalculationData);
      console.log('🧮 Tax calculation result:', taxSummary);
      
      // Update form data with calculated values
      updatedData.federalTax = taxSummary.federal || 0;
      updatedData.stateTax = taxSummary.state || 0;
      updatedData.totalTax = taxSummary.total || 0;
      updatedData.netAmount = taxSummary.netAnnual || 0;
    }
    
    setEditFormData(updatedData);
  };
  
  // Handle edit form save
  const handleEditSave = async () => {
    try {
      setIsSaving(true);
      console.log('💾 Saving other income with data:', editFormData);
      
      // Extract income and bucket IDs properly
      const incomeId = extractIncomeId(income);
      const bucketId = income.bucketId || income.bucket?._id || income.bucket;
      
      if (!incomeId || !bucketId) {
        console.error('❌ Missing required IDs for save:', { incomeId, bucketId });
        return;
      }
      
      // Prepare update data in expected format
      const updateData = {
        name: editFormData.name,
        amount: parseFloat(editFormData.grossAmount),
        frequency: (editFormData.frequency || 'monthly').toLowerCase(),
        state: editFormData.state,
        startDate: editFormData.startDate,
        endDate: editFormData.endDate || null,
        source: editFormData.source,
        description: editFormData.description
      };
      
      console.log('💾 Formatted update data:', updateData);
      
      // Call useIncome hook with correct parameters: (bucketId, incomeId, incomeData)
      await updateIncome(bucketId, incomeId, updateData);
      
      setIsEditMode(false);
      refreshIncomes();
      console.log('✅ Other income updated successfully');
    } catch (error) {
      console.error('❌ Error saving other income:', error);
    } finally {
      setIsSaving(false);
    }
  };
  
  // Handle income deletion
  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      // Extract bucketId from income object
      const bucketId = income.bucketId || (income.bucket?._id) || income.bucket;
      
      if (!bucketId) {
        console.error('❌ Cannot delete income: missing bucketId');
        setToast({
          message: 'Error: Could not determine bucket for this income',
          type: 'error'
        });
        return;
      }
      
      console.log(`🗑️ Deleting income ${income._id} from bucket ${bucketId}...`);
      await deleteIncome(bucketId, income._id);
      setShowDeleteModal(false);
      refreshIncomes();
    } catch (err) {
      console.error('❌ Error deleting income:', err);
      setToast({
        message: 'Error deleting income: ' + (err.message || 'Unknown error'),
        type: 'error'
      });
    } finally {
      setIsDeleting(false);
    }
  };
  
  // Toggle expanded view
  const toggleExpanded = () => {
    setExpanded(!expanded);
  };
  
  // Handle edit button click
  const handleEditClick = () => {
    initializeEditForm();
    setIsEditMode(true);
    setExpanded(true); // Auto-expand details when editing
  };
  
  // Handle edit cancel
  const handleEditCancel = () => {
    setIsEditMode(false);
    setEditFormData({});
  };

  // Use the utility function to normalize income data
  const { grossAmount, netAmount, frequency, taxes, type } = normalizeIncomeData(income);
  
  // Normalize income data for display safety
  const normalizedIncome = {
    _id: income._id || 'unknown',
    name: income.name || 'Unnamed Income',
    type: type || 'OTHER',
    grossAmount,
    netAmount,
    frequency,
    startDate: income.startDate ? new Date(income.startDate) : new Date(),
    endDate: income.endDate ? new Date(income.endDate) : null,
    source: income.source || 'Not specified',
    location: income.location || { state: 'Not specified' },
    description: income.description || '',
    deductions: income.deductions || {}
  };
  
  // Calculate tax summary
  const taxSummary = calculateTaxSummary(income);
  const formattedTaxSummary = formatTaxSummary(taxSummary, income.grossAmount || income.amount);
  
  // Monthly and annual calculations
  const calculatedMonthlyAmount = calculateMonthlyAmount(normalizedIncome.grossAmount, normalizedIncome.frequency);
  const calculatedAnnualAmount = calculateAnnualAmount(normalizedIncome.grossAmount, normalizedIncome.frequency);
  
  const calculatedMonthlyNet = calculateMonthlyAmount(normalizedIncome.netAmount || normalizedIncome.grossAmount * 0.8, normalizedIncome.frequency);
  const calculatedAnnualNet = calculateAnnualAmount(normalizedIncome.netAmount || normalizedIncome.grossAmount * 0.8, normalizedIncome.frequency);
  
  // Get appropriate icon for income type
  const incomeIcon = getIncomeTypeIcon(normalizedIncome.type);
  const isRental = normalizedIncome.type === 'RENTAL';
  
  // Get gradient based on income type
  const getTypeGradient = () => {
    switch (normalizedIncome.type) {
      case 'RENTAL':
        return 'from-emerald-50 to-white';
      case 'BUSINESS':
        return 'from-amber-50 to-white';
      case 'INVESTMENT':
        return 'from-purple-50 to-white';
      default:
        return 'from-gray-50 to-white';
    }
  };

  // Format income type for display
  const formatIncomeType = (type) => {
    switch (type) {
      case 'RENTAL': return 'Rental Income';
      case 'BUSINESS': return 'Business Income';
      case 'INVESTMENT': return 'Investment Income';
      case 'ROYALTY': return 'Royalty Income';
      case 'PENSION': return 'Pension';
      case 'SOCIAL_SECURITY': return 'Social Security';
      default: return type?.charAt(0) + type?.slice(1).toLowerCase() || 'Other Income';
    }
  };
  
  // Helper functions for calculating monthly and annual amounts
  function calculateMonthlyAmount(amount, frequency) {
    if (!amount) return 0;
    
    switch (frequency) {
      case 'WEEKLY':
        return amount * 52 / 12;
      case 'BI_WEEKLY':
        return amount * 26 / 12;
      case 'MONTHLY':
        return amount;
      case 'QUARTERLY':
        return amount / 3;
      case 'ANNUALLY':
        return amount / 12;
      case 'ONE_TIME':
        return 0; // One-time income doesn't have a monthly equivalent
      default:
        return amount;
    }
  }

  function calculateAnnualAmount(amount, frequency) {
    if (!amount) return 0;
    
    switch (frequency) {
      case 'WEEKLY':
        return amount * 52;
      case 'BI_WEEKLY':
        return amount * 26;
      case 'MONTHLY':
        return amount * 12;
      case 'QUARTERLY':
        return amount * 4;
      case 'ANNUALLY':
        return amount;
      case 'ONE_TIME':
        return amount; // One-time income is counted for the year
      default:
        return amount * 12;
    }
  }
  
  // Get appropriate icon component based on income type
  const getIconComponent = () => {
    switch (normalizedIncome.type) {
      case 'RENTAL':
        return <BuildingOfficeIcon className="h-5 w-5" />;
      case 'BUSINESS':
        return <DocumentTextIcon className="h-5 w-5" />;
      case 'INVESTMENT':
        return <CurrencyDollarIcon className="h-5 w-5" />;
      default:
        return <CurrencyDollarIcon className="h-5 w-5" />;
    }
  };
  
  // Get appropriate background color for the icon
  const getIconBgColor = () => {
    switch (normalizedIncome.type) {
      case 'RENTAL':
        return 'bg-emerald-100 text-emerald-600';
      case 'BUSINESS':
        return 'bg-amber-100 text-amber-600';
      case 'INVESTMENT':
        return 'bg-purple-100 text-purple-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };
  
  // Render expanded details
  const renderExpandedDetails = () => {
    if (!expanded) return null;
    
    return (
      <div className="p-4 bg-gray-50 border-t border-gray-200">
        {/* Basic Income Details Section */}
        <div className="mb-4">
          <h3 className="text-sm font-medium text-gray-700 mb-2">Income Details</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-gray-600 mb-1">Income Name</p>
              {isEditMode ? (
                <input
                  type="text"
                  value={editFormData.name || ''}
                  onChange={(e) => handleEditFieldChange('name', e.target.value)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter income name"
                />
              ) : (
                <p className="text-sm font-medium text-gray-900">{normalizedIncome.name}</p>
              )}
            </div>
            <div>
              <p className="text-xs text-gray-600 mb-1">Source</p>
              {isEditMode ? (
                <input
                  type="text"
                  value={editFormData.source || ''}
                  onChange={(e) => handleEditFieldChange('source', e.target.value)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter income source"
                />
              ) : (
                <p className="text-sm font-medium text-gray-900">{normalizedIncome.source || 'Not specified'}</p>
              )}
            </div>
            <div>
              <p className="text-xs text-gray-600 mb-1">Location (State)</p>
              {isEditMode ? (
                <select
                  value={editFormData.state || ''}
                  onChange={(e) => handleEditFieldChange('state', e.target.value)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  {STATE_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              ) : (
                <p className="text-sm font-medium text-gray-900">{normalizedIncome.state || 'Not specified'}</p>
              )}
            </div>
            <div>
              <p className="text-xs text-gray-600 mb-1">Source Type</p>
              <p className="text-sm font-medium text-gray-900">{formatIncomeType(normalizedIncome.type)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-600 mb-1">Start Date</p>
              {isEditMode ? (
                <input
                  type="date"
                  value={editFormData.startDate || ''}
                  onChange={(e) => handleEditFieldChange('startDate', e.target.value)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
              ) : (
                <p className="text-sm font-medium text-gray-900">
                  {normalizedIncome.startDate ? formatDate(normalizedIncome.startDate, 'MMM d, yyyy') : 'Not specified'}
                </p>
              )}
            </div>
            <div>
              <p className="text-xs text-gray-600 mb-1">End Date (Optional)</p>
              {isEditMode ? (
                <input
                  type="date"
                  value={editFormData.endDate || ''}
                  onChange={(e) => handleEditFieldChange('endDate', e.target.value)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
              ) : (
                <p className="text-sm font-medium text-gray-900">
                  {normalizedIncome.endDate ? formatDate(normalizedIncome.endDate, 'MMM d, yyyy') : 'Ongoing'}
                </p>
              )}
            </div>
          </div>
          
          {/* Description */}
          {isEditMode && (
            <div className="mt-4">
              <p className="text-xs text-gray-600 mb-1">Description/Notes</p>
              <textarea
                value={editFormData.description || ''}
                onChange={(e) => handleEditFieldChange('description', e.target.value)}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter any additional notes or description"
                rows={3}
              />
            </div>
          )}
        </div>
        
        {/* Payment Details Section */}
        <div className="mb-4">
          <h3 className="text-sm font-medium text-gray-700 mb-2">Payment Details</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-gray-600 mb-1">Gross Amount</p>
              {isEditMode ? (
                <input
                  type="number"
                  value={editFormData.grossAmount || ''}
                  onChange={(e) => handleEditFieldChange('grossAmount', e.target.value)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0.00"
                  step="0.01"
                />
              ) : (
                <p className="text-sm font-medium text-gray-900">{formatCurrency(normalizedIncome.grossAmount)}</p>
              )}
            </div>
            <div>
              <p className="text-xs text-gray-600 mb-1">Payment Frequency</p>
              {isEditMode ? (
                <select
                  value={editFormData.frequency || 'monthly'}
                  onChange={(e) => handleEditFieldChange('frequency', e.target.value)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Bi-weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="annually">Annually</option>
                  <option value="one_time">One-time</option>
                </select>
              ) : (
                <p className="text-sm font-medium text-gray-900">{getFrequencyLabel(normalizedIncome.frequency)}</p>
              )}
            </div>
            <div>
              <p className="text-xs text-gray-600 mb-1">Monthly Equivalent (Auto-calculated)</p>
              <p className="text-sm font-medium text-gray-900 bg-blue-50 px-2 py-1 rounded">
                {formatCurrency(isEditMode ? 
                  calculateMonthlyAmount(parseFloat(editFormData.grossAmount) || 0, editFormData.frequency || 'monthly') :
                  calculateMonthlyAmount(normalizedIncome.grossAmount, normalizedIncome.frequency)
                )}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-600 mb-1">Annual Equivalent (Auto-calculated)</p>
              <p className="text-sm font-medium text-gray-900 bg-blue-50 px-2 py-1 rounded">
                {formatCurrency(isEditMode ? 
                  calculateAnnualAmount(parseFloat(editFormData.grossAmount) || 0, editFormData.frequency || 'monthly') :
                  calculateAnnualAmount(normalizedIncome.grossAmount, normalizedIncome.frequency)
                )}
              </p>
            </div>
          </div>
        </div>
        
        {/* Tax Information Section (Auto-calculated) */}
        <div className="mb-4">
          <h3 className="text-sm font-medium text-gray-700 mb-2">
            Tax Information {isEditMode && <span className="text-xs text-blue-600">(Auto-calculated)</span>}
            {!isEditMode && formattedTaxSummary && (
              <span className="ml-2 text-amber-600 text-xs font-normal">
                (Estimated)
              </span>
            )}
          </h3>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-xs text-gray-600 mb-1">Federal Income Tax</p>
              <div className="w-full px-2 py-1 text-sm bg-gray-50 border border-gray-200 rounded text-gray-700">
                {formatCurrency(isEditMode ? (editFormData.federalTax || 0) : (formattedTaxSummary?.federal || 0))}
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-600 mb-1">State Income Tax</p>
              <div className="w-full px-2 py-1 text-sm bg-gray-50 border border-gray-200 rounded text-gray-700">
                {formatCurrency(isEditMode ? (editFormData.stateTax || 0) : (formattedTaxSummary?.state || 0))}
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-600 mb-1">Total Tax</p>
              <div className="w-full px-2 py-1 text-sm bg-gray-50 border border-gray-200 rounded text-gray-700 font-semibold">
                {formatCurrency(isEditMode ? (editFormData.totalTax || 0) : (formattedTaxSummary?.total || 0))}
              </div>
            </div>
          </div>
          
          {/* Net Income Summary */}
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded">
            <p className="text-xs text-gray-600 mb-1">Estimated Net Annual Income</p>
            <div className="text-lg font-semibold text-green-800">
              {formatCurrency(isEditMode ? (editFormData.netAmount || 0) : (calculatedAnnualNet || normalizedIncome.netAmount || 0))}
            </div>
          </div>
        </div>
        
        {/* Type-Specific Details */}
        {normalizedIncome.type === 'RENTAL' && (
          <div className="mb-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Rental Income Details</h3>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <p className="text-xs text-gray-600">Property Address</p>
                <p className="text-sm font-medium text-gray-900">
                  {normalizedIncome.location.propertyAddress || 'Not specified'}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-600">Property Type</p>
                <p className="text-sm font-medium text-gray-900">
                  {normalizedIncome.location.propertyType || 'Not specified'}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-600">Monthly Expenses</p>
                <p className="text-sm font-medium text-gray-900">
                  {formatCurrency(normalizedIncome.deductions.rental?.monthlyExpenses || 0)}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-600">Net Income</p>
                <p className="text-sm font-medium text-gray-900">
                  {formatCurrency((normalizedIncome.grossAmount || 0) - (normalizedIncome.deductions.rental?.monthlyExpenses || 0))}
                </p>
              </div>
            </div>
          </div>
        )}
        
        {normalizedIncome.type === 'INVESTMENT' && (
          <div className="mb-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Investment Income Details</h3>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <p className="text-xs text-gray-600">Investment Type</p>
                <p className="text-sm font-medium text-gray-900">
                  {normalizedIncome.typeSpecificDetails.investment?.investmentType || 'Not specified'}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-600">Institution</p>
                <p className="text-sm font-medium text-gray-900">
                  {normalizedIncome.typeSpecificDetails.investment?.institution || 'Not specified'}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-600">Account Type</p>
                <p className="text-sm font-medium text-gray-900">
                  {normalizedIncome.typeSpecificDetails.investment?.accountType || 'Not specified'}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-600">Yield/Return Rate</p>
                <p className="text-sm font-medium text-gray-900">
                  {normalizedIncome.typeSpecificDetails.investment?.yieldRate 
                    ? `${normalizedIncome.typeSpecificDetails.investment.yieldRate}%` 
                    : 'Not specified'}
                </p>
              </div>
            </div>
          </div>
        )}
        
        {normalizedIncome.type === 'BUSINESS' && (
          <div className="mb-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Business Income Details</h3>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <p className="text-xs text-gray-600">Self-Employment Tax</p>
                <p className="text-sm font-medium text-gray-900">
                  {formatCurrency(normalizedIncome.deductions.selfEmployment || 0)}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-600">Net Income</p>
                <p className="text-sm font-medium text-gray-900">
                  {formatCurrency((normalizedIncome.grossAmount || 0) - (normalizedIncome.deductions.selfEmployment || 0))}
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Additional Notes Section (if any) */}
        {normalizedIncome.description && (
          <div className="mb-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Notes</h3>
            <p className="text-sm text-gray-900">{normalizedIncome.description}</p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`relative bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200 ${className}`}>
      {/* Header - Matching W2/Salary card style */}
      <div className={`px-4 py-3 bg-gradient-to-r ${getTypeGradient()} border-b border-gray-200 flex justify-between items-center`}>
        <div className="flex items-center space-x-3 flex-1 min-w-0">
          <div className={`p-2 rounded-lg ${getIconBgColor()}`}>
            {getIconComponent()}
          </div>
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <h3 className="font-medium text-gray-900 truncate">{normalizedIncome.name}</h3>
            {isPartnerIncome && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700 whitespace-nowrap">
                <UserGroupIcon className="h-3 w-3" />
                {ownerName}'s
              </span>
            )}
          </div>
        </div>

      </div>



      {/* Content */}
      <div className="p-3 sm:p-4 space-y-3 sm:space-y-4">
        {/* Payment Information - Mobile Responsive */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <div className="bg-gray-50 p-3 rounded-lg">
            <div className="text-xs sm:text-sm font-medium text-gray-500 mb-1">Gross Pay</div>
            <div className="text-base sm:text-lg font-semibold text-gray-900">
              {formatCurrency(normalizedIncome.grossAmount)}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {getFrequencyLabel(normalizedIncome.frequency).toLowerCase()}
            </div>
          </div>
          <div className="bg-gray-50 p-3 rounded-lg">
            <div className="text-xs sm:text-sm font-medium text-gray-500 mb-1">Gross Annual</div>
            <div className="text-base sm:text-lg font-semibold text-gray-900">
              {formatCurrency(calculatedAnnualAmount)}
            </div>
          </div>
          <div className="bg-green-50 p-3 rounded-lg">
            <div className="text-xs sm:text-sm font-medium text-gray-500 mb-1">Est. Net Monthly</div>
            <div className="text-base sm:text-lg font-semibold text-green-600">
              {formatCurrency(calculatedMonthlyNet)}
            </div>
          </div>
          <div className="bg-green-50 p-3 rounded-lg">
            <div className="text-xs sm:text-sm font-medium text-gray-500 mb-1">Est. Net Annual</div>
            <div className="text-base sm:text-lg font-semibold text-green-600">
              {formatCurrency(calculatedAnnualNet)}
            </div>
          </div>
        </div>

        {/* Expand/Collapse Toggle with Edit/Delete Actions */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center pt-2 border-t border-gray-200 gap-2 sm:gap-0">
          <button
            onClick={toggleExpanded}
            className="flex items-center space-x-1 text-sm text-blue-600 hover:text-blue-800 transition-colors"
          >
            <span>{expanded ? 'Hide Details' : 'Show Details'}</span>
            {expanded ? (
              <ChevronUpIcon className="h-4 w-4" />
            ) : (
              <ChevronDownIcon className="h-4 w-4" />
            )}
          </button>
          
          {/* Edit/Delete/Save/Cancel Actions in Toggle Section */}
          <div className="flex items-center space-x-2">
            {isEditMode ? (
              <>
                <button
                  onClick={handleEditSave}
                  disabled={isSaving}
                  className="flex items-center space-x-1 px-3 py-2 bg-green-50 text-green-600 hover:text-green-800 rounded hover:bg-green-100 transition-colors text-xs disabled:opacity-50 min-h-[36px]"
                  aria-label="Save changes"
                  title="Save changes"
                >
                  <span>{isSaving ? 'Saving...' : 'Save'}</span>
                </button>
                <button
                  onClick={handleEditCancel}
                  className="flex items-center space-x-1 px-3 py-2 bg-gray-50 text-gray-600 hover:text-gray-800 rounded hover:bg-gray-100 transition-colors text-xs min-h-[36px]"
                  aria-label="Cancel editing"
                  title="Cancel editing"
                >
                  <span>Cancel</span>
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleEditClick}
                  className="flex items-center space-x-1 px-3 py-2 bg-blue-50 text-blue-600 hover:text-blue-800 rounded hover:bg-blue-100 transition-colors text-xs min-h-[36px]"
                  aria-label="Edit income"
                  title="Edit income"
                >
                  <PencilIcon className="h-3 w-3" />
                  <span className="hidden sm:inline">Edit</span>
                </button>
                <button
                  onClick={() => setShowDeleteModal(true)}
                  className="flex items-center space-x-1 px-3 py-2 bg-red-50 text-red-500 hover:text-red-700 rounded hover:bg-red-100 transition-colors text-xs min-h-[36px]"
                  aria-label="Delete income"
                  title="Delete income"
                >
                  <TrashIcon className="h-3 w-3" />
                  <span className="hidden sm:inline">Delete</span>
                </button>
              </>
            )}
            <div className="text-xs text-gray-500 hidden sm:block">
              Started {formatDate(normalizedIncome.startDate, 'MMM d, yyyy')}
            </div>
          </div>
        </div>
        
        {/* Expanded details */}
        {renderExpandedDetails()}
      </div>
      
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
            <div className="flex items-center text-red-600 mb-4">
              <ExclamationTriangleIcon className="h-6 w-6 mr-2" />
              <h3 className="text-lg font-semibold">Confirm Deletion</h3>
            </div>
            
            <p className="mb-4 text-gray-600">
              Are you sure you want to delete <span className="font-semibold">{normalizedIncome.name}</span>? 
              This action cannot be undone.
            </p>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700 rounded border border-red-200 transition-colors"
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Also provide a default export for backwards compatibility
export default OtherIncomeCard; 